import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./api";
import { useSession } from "./auth";
import { useRooms } from "./rooms";
import { taskApi } from "@/features/tasks/api";
import type { Task } from "./types";
import { toClientMessage, type ServerMessage } from "./messages";

export function useTaskSummary(roomId?: string) {
  const session = useSession();
  return useQuery({ queryKey: ["task-summary", session?.user.id, roomId], enabled: !!session,
    queryFn: async (): Promise<Task[]> => {
      const result = await taskApi.listTasks({ room_id: roomId });
      return result.tasks.map(t => ({ id: t.id, title: t.title, description: t.description || undefined, status: t.status === "doing" ? "in_progress" : t.status, priority: ({ P0: "urgent", P1: "high", P2: "medium", P3: "low" } as const)[t.priority], assigneeId: t.assignee ?? undefined,
        dueAt: t.due_at ? new Date(t.due_at).toISOString() : undefined, roomId: t.room_id ?? undefined }));
    } });
}
export function usePinnedMessages(roomId?: string) {
  const { rooms } = useRooms();
  const session = useSession();
  const targets = roomId ? rooms.filter(r => r.id === roomId) : rooms;
  const key = targets.map(r => r.id).join(",");
  return useQuery({ queryKey: ["pins", session?.user.id, key], enabled: !!session && !!targets.length,
    queryFn: async () => {
      const lists = await Promise.all(targets.map(async room => {
        const result = await apiGet<{ pins: { message_id: string }[] }>(`/v1/rooms/${encodeURIComponent(room.id)}/pins`);
        if (result.pins.length === 0) return [];
        const [memberList, messages] = await Promise.all([
          apiGet<{ members: { user_id: string; display_name?: string | null; email?: string }[] }>(`/v1/rooms/${encodeURIComponent(room.id)}/members`),
          Promise.all(result.pins.map(pin => apiGet<{ message: ServerMessage }>(`/v1/messages/${encodeURIComponent(pin.message_id)}`))),
        ]);
        const memberNames = new Map(memberList.members.map(member => [member.user_id, member.display_name || member.email]));
        return messages.map(response => {
          const message = toClientMessage(response.message, session?.user.id);
          return {
            id: message.id,
            roomId: room.id,
            roomName: room.name,
            author: message.sender?.display_name || memberNames.get(message.authorId) || message.authorName
              || (message.authorId === session?.user.id ? session.user.displayName : undefined),
            preview: message.body.slice(0, 180),
          };
        });
      }));
      return lists.flat();
    } });
}
